import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  NbCardModule,
  NbSpinnerModule,
  NbButtonModule,
  NbInputModule,
  NbDialogRef,
  NbCheckboxModule,
} from '@nebular/theme';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { NgStyle } from '@angular/common';
import { APIService, KPI } from '../../../../@core/api';
import { SaveToastService } from '../../../../@core/utils/save-toast.service';

@Component({
  selector: 'app-site-users-form',
  templateUrl: './site-users-form.component.html',
  styleUrls: ['../base-form/base-form.component.scss', './site-users-form.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    NbInputModule,
    NbCheckboxModule,
    TranslateModule,
    FormsModule,
    NgStyle,
  ],
})
export class SiteUsersFormComponent implements OnInit, OnDestroy {
  @Input() siteId: number;
  @Input() siteName: string = '';

  public loading = true;
  public searchText = '';
  public allUsers: KPI.User[] = [];
  public permissions: KPI.PerimeterPermission[] = [];
  private subscriptions: Subscription[] = [];
  private togglingUsers: Set<number> = new Set();

  constructor(
    private nbDialogRef: NbDialogRef<SiteUsersFormComponent>,
    private api: APIService,
    private saveToast: SaveToastService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loading = true;
    this.subscriptions.push(
      this.api.users_members(null, true).subscribe((users) => {
        this.allUsers = users;
        this.loadPermissions();
      }),
    );
  }

  private loadPermissions(): void {
    this.subscriptions.push(
      this.api.users_perimeter_permissions({ perimeter: this.siteId }).subscribe((permissions) => {
        this.permissions = permissions;
        this.loading = false;
      }),
    );
  }

  public get filteredUsers(): KPI.User[] {
    if (!this.searchText) return this.allUsers;
    const search = this.searchText.toLowerCase();
    return this.allUsers.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
      return name.includes(search);
    });
  }

  public isUserAssigned(user: KPI.User): boolean {
    return this.permissions.some((p) => p.user === user.get_user_id);
  }

  public toggleUser(user: KPI.User): void {
    const userId = user.get_user_id;
    if (this.togglingUsers.has(userId)) return;
    this.togglingUsers.add(userId);

    const existing = this.permissions.find((p) => p.user === userId);
    this.saveToast.showSaving();
    if (existing) {
      this.subscriptions.push(
        this.api.delete_perimeter_permissions(existing.id).subscribe(() => {
          this.permissions = this.permissions.filter((p) => p.id !== existing.id);
          this.togglingUsers.delete(userId);
          this.saveToast.showSaved();
        }),
      );
    } else {
      this.subscriptions.push(
        this.api.post_perimeter_permissions({
          perimeter: this.siteId,
          user: userId,
        }).subscribe((permission) => {
          this.permissions = [...this.permissions, permission];
          this.togglingUsers.delete(userId);
          this.saveToast.showSaved();
        }),
      );
    }
  }

  public getUserName(user: KPI.User): string {
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : user.email || `User ${user.id}`;
  }

  public close(): void {
    this.nbDialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
